import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useDeferredValue } from "react";
import { z } from "zod";
import { Input } from "@/components/ui/field";
import { Empty, LastSeen, PageHeader, Rows } from "@/features/admin/parts";
import { usersQuery } from "@/lib/api/queries";
import { plural } from "@/lib/format";

export const Route = createFileRoute("/_app/admin/users/")({
  validateSearch: z.object({ q: z.string().optional() }),
  component: UsersPage,
});

function UsersPage() {
  const { q = "" } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const query = useDeferredValue(q.trim());
  const users = useQuery({ ...usersQuery(query), placeholderData: keepPreviousData });

  return (
    <>
      <PageHeader title="Пользователи" />
      <div className="relative mt-6">
        <Search
          aria-hidden
          className="absolute top-1/2 left-4 size-4 -translate-y-1/2 text-muted"
        />
        <Input
          value={q}
          onChange={(e) => navigate({ search: { q: e.target.value || undefined }, replace: true })}
          placeholder="Поиск по логину"
          aria-label="Поиск по логину"
          className="pl-10"
          type="search"
        />
      </div>
      <div className="mt-4">
        {users.data?.length === 0 ? (
          <Empty>{query ? "Никого не нашли." : "Пользователей пока нет — создайте инвайт."}</Empty>
        ) : (
          <Rows>
            {users.data?.map(({ user, note, active_configs, last_handshake_at }) => (
              <li key={user.id}>
                <Link
                  to="/admin/users/$userId"
                  params={{ userId: user.id }}
                  className="grid gap-1 px-4 py-3 hover:bg-surface-2 sm:grid-cols-[1fr_9rem_10rem] sm:items-center sm:gap-4"
                >
                  <span className="flex min-w-0 items-center gap-2 font-medium">
                    {user.username}
                    {note && (
                      <span className="truncate text-sm font-normal text-muted">{note}</span>
                    )}
                    {user.role === "admin" && (
                      <span className="text-xs text-amber-text">админ</span>
                    )}
                    {user.status === "blocked" && (
                      <span className="text-xs text-danger">заблокирован</span>
                    )}
                  </span>
                  <span className="text-sm text-muted">
                    {active_configs} {plural(active_configs, ["конфиг", "конфига", "конфигов"])}
                  </span>
                  <span className="text-sm sm:text-right">
                    <LastSeen iso={last_handshake_at} />
                  </span>
                </Link>
              </li>
            ))}
          </Rows>
        )}
      </div>
    </>
  );
}
